<template>
    <li :id="'reactie-' + comment.id" class="comment-thread">
        <article class="comment" :class="{'comment-staff': comment.isStaff, 'comment-new': comment.isNew, 'comment-flash': store.flashedId.value === comment.id}">
            <transition name="comment-fade" mode="out-in">
                <form v-if="editing" key="edit" novalidate @submit.prevent="save">
                    <div class="form-floating mb-3">
                        <input :id="'comment-edit-' + comment.id + '-name'" v-model="form.name" type="text" class="form-control" :class="{'is-invalid': errors.name}"
                               placeholder="Naam" maxlength="100">
                        <label :for="'comment-edit-' + comment.id + '-name'">Naam</label>
                        <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                    </div>
                    <div class="form-floating mb-3">
                        <textarea :id="'comment-edit-' + comment.id + '-body'" v-model="form.body" class="form-control comment-textarea" :class="{'is-invalid': errors.body}"
                                  placeholder="Reactie" maxlength="2000"></textarea>
                        <label :for="'comment-edit-' + comment.id + '-body'">Reactie</label>
                        <div v-if="errors.body" class="invalid-feedback">{{ errors.body }}</div>
                    </div>
                    <div v-if="message" class="alert alert-danger" role="status">{{ message }}</div>
                    <button type="submit" class="btn btn-primary btn-sm me-2" :disabled="busy">Opslaan</button>
                    <button type="button" class="btn btn-secondary btn-sm" :disabled="busy" @click="editing = false">Annuleren</button>
                </form>

                <div v-else key="view">
                    <div class="comment-header">
                        <span class="comment-name">{{ comment.name }}</span>
                        <span v-if="comment.isStaff" class="comment-badge">GoedGebruik</span>
                        <time class="comment-date" :datetime="comment.dateTime">{{ comment.date }}</time>
                        <span v-if="comment.isEdited" class="comment-edited">aangepast</span>
                    </div>
                    <a v-if="flattened" :href="'#reactie-' + comment.parentId" class="comment-reply-to" @click.prevent="store.showComment(comment.parentId)">
                        in reactie op {{ store.nameOf(comment.parentId) }}
                    </a>
                    <p class="comment-body">{{ comment.body }}</p>

                    <div class="comment-actions">
                        <button type="button" class="btn btn-link btn-sm" :aria-expanded="replying ? 'true' : 'false'"
                                @click="replying = !replying">Reageren</button>
                        <template v-if="store.canModerate">
                            <button type="button" class="btn btn-link btn-sm" @click="startEditing">Aanpassen</button>
                            <button type="button" class="btn btn-link btn-sm comment-delete" :disabled="busy" @click="destroy">Verwijderen</button>
                        </template>
                    </div>
                    <div v-if="message" class="alert alert-danger mt-2 mb-0" role="status">{{ message }}</div>
                </div>
            </transition>
        </article>

        <transition-group tag="ol" class="comment-replies list-unstyled" :class="{'comment-replies-flat': repliesFlattened}" :css="false" @enter="expand" @leave="collapse">
            <comment-item v-for="reply in replies" :key="reply.id" :comment="reply" :depth="depth + 1"/>
        </transition-group>

        <transition :css="false" @enter="expand" @leave="collapse">
            <comment-form v-if="replying" :parent-id="comment.id" :reply-to="comment.name" @created="replying = false" @cancel="replying = false"/>
        </transition>
    </li>
</template>

<script setup>
import {computed, inject, reactive, ref} from 'vue';
import swal from 'sweetalert2';
import {api, describeError} from './api.js';
import CommentForm from './CommentForm.vue';
import {collapse, expand} from './motion.js';

const props = defineProps({
    comment: {type: Object, required: true},
    // 0 voor een reactie op het item zelf, 1 voor een antwoord daarop, enzovoort.
    depth:   {type: Number, default: 0}
});

const store = inject('comments');

const replies = computed(() => store.childrenOf(props.comment.id));

// Inspringen stopt bij maxVisualDepth niveaus (config/comments.php); reageren kan onbeperkt door.
// Dieper staan antwoorden op het laatste niveau, naast hun ouder, met "in reactie op" erbij.
const repliesFlattened = computed(() => props.depth >= store.maxVisualDepth - 1);
const flattened        = computed(() => props.depth >= store.maxVisualDepth);

const editing  = ref(false);
const replying = ref(false);
const busy     = ref(false);
const form     = reactive({name: '', body: ''});
const errors   = ref({});
const message  = ref('');

const startEditing = () => {
    form.name     = props.comment.name;
    form.body     = props.comment.body;
    errors.value  = {};
    message.value = '';
    editing.value = true;
};

const save = async () => {
    // Niets veranderd: gewoon sluiten. De server zou het ook niet als aangepast markeren, maar zo
    // gaat er ook geen verzoek heen.
    if(form.name.trim() === props.comment.name && form.body.trim() === props.comment.body) {
        editing.value = false;

        return;
    }

    busy.value    = true;
    errors.value  = {};
    message.value = '';

    try {
        const response = await api.put('/reacties/' + props.comment.id, form);

        store.replaceComment(response.data.comment);
        editing.value = false;
    } catch(error) {
        const described = describeError(error);

        errors.value  = described.fields;
        message.value = described.message;
    } finally {
        busy.value = false;
    }
};

const destroy = async () => {
    const replyCount = store.descendantIds(props.comment.id).length;
    const andReplies = replyCount === 0 ? '' : (replyCount === 1 ? ' en het antwoord daarop' : ' en de ' + replyCount + ' antwoorden daarop');

    const result = await swal.fire({
        title:              'Reactie verwijderen?',
        text:               'De reactie van ' + props.comment.name + andReplies + ' ' + (replyCount ? 'zijn' : 'is') + ' daarna weg. Dat kun je niet ongedaan maken.',
        icon:               'warning',
        showCancelButton:   true,
        confirmButtonText:  'Verwijderen',
        confirmButtonColor: '#FC3A5A',
        cancelButtonText:   'Annuleren'
    });

    if(!result.isConfirmed) {
        return;
    }

    busy.value    = true;
    message.value = '';

    try {
        await api.delete('/reacties/' + props.comment.id);
        store.removeComment(props.comment.id);
    } catch(error) {
        message.value = describeError(error).message;
        busy.value    = false;
    }
};
</script>
